import React from 'react';
import {
    // Plomberie
    Wrench,
    // Électricité
    Zap,
    // Peinture
    Paintbrush,
    // Menuiserie bois
    Hammer,
    // Climatisation
    Fan,
    // Nettoyage
    SprayCan,
    // Jardinage
    Scissors,
    // Déménagement
    Truck,
    // Menuiserie aluminium
    DoorOpen,
    // Transport
    Car,
    // Soudure
    Flame,
    // Vitrier - Fenêtres/Miroirs
    Maximize2,
    Layers,
    // Default
    Briefcase,
    // Carrelage
    Grid3x3,
    // Serrurerie
    KeyRound
} from 'lucide-react';

/**
 * Maps service category slugs to their specific icons.
 * Each service has a carefully chosen icon that represents its trade.
 * 
 * SERVICES:
 * - Plomberie = Wrench (outil de plombier)
 * - Électricité = Zap (éclair pour l'électricité)
 * - Peinture = Paintbrush (pinceau)
 * - Menuiserie Bois = Hammer (marteau de menuisier)
 * - Menuiserie Aluminium = DoorOpen (porte/fenêtre alu)
 * - Climatisation = Fan (ventilateur/clim)
 * - Nettoyage = SprayCan (spray nettoyant)
 * - Jardinage = Scissors (sécateur)
 * - Déménagement = Truck (camion de déménagement)
 * - Transport = Car (voiture/véhicule)
 * - Soudure = Flame (flamme du chalumeau)
 * - Vitrier = Maximize2 (cadre de fenêtre)
 */
export const getCategoryIcon = (slug?: string, iconName?: string, className: string = "w-5 h-5") => {
    const key = (slug || iconName || '').toLowerCase();

    // === PLOMBERIE (Plumbing) ===
    // Robinets, tuyaux, fuites, salles de bain
    if (key.includes('plomb') || key.includes('plumb') || key === 'wrench') {
        return <Wrench className={className} />;
    }

    // === ÉLECTRICITÉ (Electrical) ===
    // Câblage, prises, disjoncteurs, éclairage
    if (key.includes('electr') || key === 'zap' || key === 'plug') {
        return <Zap className={className} />;
    }

    // === PLATRERIE / STAFF (Plastering / Gypsum Decor) ===
    // Corniches, moulures, faux plafonds, decorations en jbess
    if (key.includes('platr') || key.includes('staff') || key.includes('plaster') || key.includes('gyps') || key === 'layers') {
        return <Layers className={className} />;
    }

    // === PEINTURE (Painting) ===
    // Peinture intérieure/extérieure, papier peint, décoration
    if (key.includes('peint') || key.includes('paint') || key === 'paintbrush' || key === 'brush') {
        return <Paintbrush className={className} />;
    }

    // === MENUISERIE BOIS (Woodworking/Carpentry) ===
    // Portes, placards, meubles, structures en bois
    if ((key.includes('menuis') && !key.includes('alum')) || key.includes('carpent') || key === 'hammer' || key.includes('bois') || key.includes('wood')) {
        return <Hammer className={className} />;
    }

    // === MENUISERIE ALUMINIUM (Aluminum Work) ===
    // Fenêtres, portes, façades en aluminium
    if (key.includes('alum') || key.includes('aluminum') || key.includes('aluminium')) {
        return <DoorOpen className={className} />;
    }

    // === CLIMATISATION (Air Conditioning / HVAC) ===
    // Installation clim, chauffage, ventilation
    if (key.includes('clim') || key.includes('air') || key.includes('hvac') || key === 'wind' || key === 'fan') {
        return <Fan className={className} />;
    }

    // === NETTOYAGE / MENAGE (Cleaning / Housekeeping) ===
    // Ménage, nettoyage en profondeur, nettoyage commercial
    // "demenagement" contains "menage": moving must be tested before cleaning.
    if (key.includes('demen') || key.includes('moving')) {
        return <Truck className={className} />;
    }
    if (key.includes('nettoy') || key.includes('menage') || key.includes('ménage') || key.includes('clean') || key === 'sparkles' || key === 'spraycan') {
        return <SprayCan className={className} />;
    }

    // === JARDINAGE (Gardening/Landscaping) ===
    // Pelouse, plantation, taille d'arbres, aménagement paysager
    if (key.includes('jardin') || key.includes('garden') || key.includes('landscap') || key === 'scissors') {
        return <Scissors className={className} />;
    }


    // === TRANSPORT ===
    // Livraison, services de transport
    if (key.includes('transport') || key.includes('delivery') || key.includes('livrai')) {
        return <Car className={className} />;
    }

    // === SOUDURE (Welding) ===
    // Soudure métallique, fabrication
    if (key.includes('soud') || key.includes('weld')) {
        return <Flame className={className} />;
    }

    // === VITRIER (Glazier/Glass Work) ===
    // Fenêtres, miroirs, installation de verre
    if (key.includes('vitr') || key.includes('glass') || key.includes('mirror')) {
        return <Maximize2 className={className} />;
    }

    // === CARRELAGE (Tiling) ===
    // Pose de carrelage, faïence, sols et murs
    if (key.includes('carrel') || key.includes('tile') || key === 'grid3x3') {
        return <Grid3x3 className={className} />;
    }

    // === SERRURERIE (Locksmith) ===
    // Ouverture de porte, changement de serrure
    if (key.includes('serrur') || key.includes('lock') || key === 'keyround') {
        return <KeyRound className={className} />;
    }

    // === DEFAULT ===
    return <Briefcase className={className} />;
};

/**
 * Get category color for consistent theming (optional use)
 */
export const getCategoryColor = (slug?: string): string => {
    const key = (slug || '').toLowerCase();

    if (key.includes('plomb')) return 'text-blue-500';
    if (key.includes('electr')) return 'text-yellow-500';
    if (key.includes('platr') || key.includes('staff') || key.includes('plaster') || key.includes('gyps')) return 'text-stone-500';
    if (key.includes('peint')) return 'text-purple-500';
    if (key.includes('menuis') && !key.includes('alum')) return 'text-amber-700';
    if (key.includes('alum')) return 'text-slate-500';
    if (key.includes('clim')) return 'text-cyan-500';
    // "demenagement" contains "menage": moving must be tested before cleaning.
    if (key.includes('demen')) return 'text-orange-500';
    if (key.includes('nettoy') || key.includes('menage')) return 'text-emerald-500';
    if (key.includes('jardin')) return 'text-green-600';
    if (key.includes('transport')) return 'text-indigo-500';
    if (key.includes('soud')) return 'text-red-500';
    if (key.includes('vitr')) return 'text-sky-400';
    if (key.includes('carrel')) return 'text-orange-600';
    if (key.includes('serrur')) return 'text-gray-600';

    return 'text-primary';
};
